/**
 * Provider-backed listings (IWG, etc.) are surfaced by the backend through
 * either a `provider` flag on the listing/workspace serializer or by the
 * `provider_status` block on booking responses. The serializer flag isn't
 * shipped yet — until it lands, we read it off whichever shape we get and
 * leave a single place to wire the canonical field once available.
 */

const readString = (value: unknown): string | null => {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
};

const asRecord = (value: unknown): Record<string, unknown> | null => {
    if (!value || typeof value !== 'object') return null;
    return value as Record<string, unknown>;
};

/**
 * Maps the stable provider *code* (`metaData.provider` is lowercase; the
 * booking-side `providerStatus.provider` is uppercase) to a user-facing
 * display name. Used only as a safety fallback — for IWG listings the BE
 * also publishes `metaData.iwgBrands` (e.g. ["Regus"], ["Regus","Spaces"]),
 * which is the canonical user-facing label. See `getListingProviderBrands`.
 * Keep keys lowercase — `getListingProviderCode` normalizes before lookup.
 */
const PROVIDER_DISPLAY_NAMES: Record<string, string> = {
    iwg: 'IWG'
};

/**
 * Legacy Sharetribe / pre-sync Regus inventory often has no
 * `metaData.provider`, but titles still start with the brand
 * (e.g. "Regus - Petaluma Meeting Room 3").
 */
const IWG_TITLE_BRAND_PREFIXES = [
    'regus',
    'spaces -',
    'spaces —',
    'signature by regus',
    'hq -',
    'hq —'
] as const;

const fromMetaData = (meta: unknown): string | null => {
    const obj = asRecord(meta);
    if (!obj) return null;
    return (
        readString(obj.provider) ??
        readString(obj.providerName) ??
        readString(obj.provider_name) ??
        null
    );
};

const fromListing = (listing: unknown): string | null => {
    const obj = asRecord(listing);
    if (!obj) return null;
    return (
        readString(obj.providerCode) ??
        readString(obj.provider) ??
        readString(obj.providerName) ??
        readString(obj.provider_name) ??
        fromMetaData(obj.metaData) ??
        fromMetaData(obj.meta_data)
    );
};

const titleLooksLikeIwgBrand = (title: string | null): boolean => {
    if (!title) return false;
    const lower = title.toLowerCase();
    return IWG_TITLE_BRAND_PREFIXES.some((prefix) => lower.startsWith(prefix));
};

/** Infer `iwg` from listing/workspace title when the provider flag is missing. */
const fromIwgBrandTitle = (listing: unknown): string | null => {
    const obj = asRecord(listing);
    if (!obj) return null;
    const workspace = asRecord(obj.workspace);
    if (
        titleLooksLikeIwgBrand(readString(obj.title)) ||
        titleLooksLikeIwgBrand(readString(workspace?.title))
    ) {
        return 'iwg';
    }
    return null;
};

/**
 * Returns the stable provider code (lowercase, e.g. `"iwg"`) for a listing,
 * or `null` for native listings. Branch on this for *logic*; use
 * `getListingProviderBrands` / `getListingProviderDisplayName` for *UI*.
 *
 * For v1 the only in-payload signal is `listing.metaData.provider` (set by
 * the IWG sync). When BE ships the canonical `providerCode` on the listing
 * serializer, this helper picks it up automatically.
 *
 * Fallbacks (QA / legacy inventory):
 * - `metaData.iwgBrands` present without `provider`
 * - title / workspace title starts with a known IWG brand (e.g. Regus)
 */
export const getListingProviderCode = (listing: unknown): string | null => {
    const obj = asRecord(listing);
    if (!obj) return null;
    const listingProvider = fromListing(obj);
    const provider =
        listingProvider ??
        fromListing(obj.workspace) ??
        (getListingProviderBrands(listing).length > 0 ? 'iwg' : null) ??
        fromIwgBrandTitle(listing);
    return provider ? provider.toLowerCase() : null;
};

const readBrandsFromMeta = (meta: unknown): string[] => {
    const obj = asRecord(meta);
    if (!obj) return [];
    const raw = obj.iwgBrands ?? obj.iwg_brands;
    if (!Array.isArray(raw)) return [];
    return raw.filter(
        (b): b is string => typeof b === 'string' && b.trim().length > 0
    );
};

/**
 * Returns the marketing brand names for a listing (e.g. `["Regus"]`,
 * `["Regus", "Spaces"]`) from `metaData.iwgBrands`. Falls back to checking
 * the embedded workspace's metaData since the BE writes brands at the
 * workspace level and cascades them to listings.
 *
 * Returns an empty array for non-provider listings or when no brands are
 * published (e.g. a stale workspace with `provider: "iwg"` but no
 * `iwgBrands` yet). Callers should fall back to
 * `getListingProviderDisplayName` for the safety label in that case.
 */
export const getListingProviderBrands = (listing: unknown): string[] => {
    const obj = asRecord(listing);
    if (!obj) return [];
    const direct = readBrandsFromMeta(obj.metaData);
    if (direct.length > 0) return direct;
    const directSnake = readBrandsFromMeta(obj.meta_data);
    if (directSnake.length > 0) return directSnake;
    const workspace = asRecord(obj.workspace);
    if (!workspace) return [];
    const wsCamel = readBrandsFromMeta(workspace.metaData);
    if (wsCamel.length > 0) return wsCamel;
    return readBrandsFromMeta(workspace.meta_data);
};

/**
 * Returns the user-facing display name for a listing's provider. Prefers
 * the published brand name(s) from `metaData.iwgBrands`:
 *   - single brand → that brand (e.g. `"Regus"`)
 *   - multiple brands → joined with " & " (e.g. `"Regus & Spaces"`)
 * Falls back to the curated provider name (e.g. `"IWG"`) when brands aren't
 * published, and `null` for native listings.
 *
 * For surfaces that should render *one chip per brand* (per BE spec), use
 * `getListingProviderBrands` directly — this helper is for string contexts
 * (modal copy, accessibility labels, the booking-sidebar "Bookable via X").
 */
export const getListingProviderDisplayName = (
    listing: unknown
): string | null => {
    const brands = getListingProviderBrands(listing);
    if (brands.length === 1) return brands[0];
    if (brands.length > 1) return brands.join(' & ');
    const code = getListingProviderCode(listing);
    if (!code) return null;
    return PROVIDER_DISPLAY_NAMES[code] ?? code.toUpperCase();
};

/**
 * Analytics fan-out of the provider signals — the stable code for
 * logic-grade filtering and the marketing brand(s) for display-grade
 * breakdowns. Key names match the PostHog property names verbatim so the
 * object spreads straight into event params. Both are always-present
 * strings ('' on native listings / unpublished brands) so PostHog
 * filters never special-case a missing key. Multi-brand listings join
 * with ", " (same convention as `listing_amenities`).
 */
export type ProviderTrackingProps = {
    provider: string;
    provider_brand: string;
};

export const getProviderTrackingProps = (
    listing: unknown
): ProviderTrackingProps => ({
    provider: getListingProviderCode(listing) ?? '',
    provider_brand: getListingProviderBrands(listing).join(', ')
});

/**
 * True when the booking returned a `providerStatus` block. BE wire is
 * always camelCase; `provider_status` fallback kept for safety only.
 */
export const isProviderBooking = (booking: unknown): boolean => {
    const obj = asRecord(booking);
    if (!obj) return false;
    return !!asRecord(obj.providerStatus) || !!asRecord(obj.provider_status);
};
